import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useCallback } from 'react';

import { type EPEventPayload, type POEventPayload } from './types';

export type { EPEventPayload, POEventPayload };

export type PostOfficeAnalyticsHookScope = {
	fireAnalyticsEvent: (event: POEventPayload, attributes?: Record<string, unknown>) => void;
};

export type EngagementPlatformAnalyticsHookScope = {
	fireAnalyticsEvent: (event: EPEventPayload, attributes?: Record<string, unknown>) => void;
};

export const ANALYTICS_CHANNEL = 'postOffice';
export const EP_ANALYTICS_CHANNEL = 'engagementPlatform';
/**
 * Hook to fire track and ui analytics events for Post Office. All events will be fired with the channel `postOffice`. PlacementId will be added to attributes via PostOfficeAnalyticsContext.
 * @returns {Object} - Object containing `fireAnalyticsEvent` function.
 */
export const useFirePostOfficeAnalyticsEvents = (): PostOfficeAnalyticsHookScope => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent = useCallback(
		<Attributes extends Record<string, unknown>>(
			eventPayload: POEventPayload,
			attributes?: Attributes,
		) => {
			createAnalyticsEvent({
				...eventPayload,
				side: 'client',
				attributes: {
					...eventPayload.attributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	return {
		fireAnalyticsEvent,
	};
};

// In-app
export const EP_IN_APP_EXPOSED_PAYLOAD: {
	eventType: string;
	action: string;
	actionSubject: string;
	source: string;
	tenantType: string;
	userType: string;
	attributes: {
		reason: string;
		ruleId: string;
		sourceProduct: string;
	};
	tags: string[];
} = {
	eventType: 'track',
	action: 'exposed',
	actionSubject: 'feature',
	source: 'engagement-platform',
	tenantType: 'cloudId',
	userType: 'atlassianAccount',
	attributes: {
		reason: 'RULE_MATCH',
		ruleId: 'engagement.inproduct',
		sourceProduct: 'postOffice',
	},
	tags: ['engagementPlatform'],
};

export const EP_EXTERNAL_EXPOSED_PAYLOAD: {
	eventType: string;
	action: string;
	actionSubject: string;
	source: string;
	tenantType: string;
	userType: string;
	attributes: {
		sourceProduct: string;
	};
	tags: string[];
	product: string;
} = {
	eventType: 'operational',
	action: 'stopped',
	actionSubject: 'message',
	source: 'epTargeting',
	tenantType: 'cloudId',
	userType: 'atlassianAccount',
	attributes: {
		sourceProduct: 'postOffice',
	},
	tags: ['engagementPlatform'],
	product: 'engagementPlatform',
};

/**
 *
 * @returns Hook to fire track and ui analytics events for EP migrated messages.
 * All events will be fired with the channel `engagementPlatform`.
 * PlacementId will be added to attributes via PostOfficeAnalyticsContext.
 * Events are automatically forwarded to GASv3 via EngagementEventsListener when feature flag is enabled.
 *  @returns {Object} - Object containing `fireAnalyticsEvent` function.
 */
export const useFireEngagementPlatformAnalyticsEvents =
	(): EngagementPlatformAnalyticsHookScope => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const fireAnalyticsEvent = useCallback(
			(eventPayload: EPEventPayload, attributes?: Record<string, unknown>) => {
				if (eventPayload.messageType === 'inapp') {
					const epMessageId = `engagement.inproduct.${eventPayload.messageId}`;

					createAnalyticsEvent({
						...EP_IN_APP_EXPOSED_PAYLOAD,
						...eventPayload,
						actionSubjectId: epMessageId,
						attributes: {
							...EP_IN_APP_EXPOSED_PAYLOAD.attributes,
							...(eventPayload.locale && { locale: eventPayload.locale }),
							...attributes,
							flagKey: epMessageId,
						},
					}).fire(EP_ANALYTICS_CHANNEL);
				} else {
					createAnalyticsEvent({
						...EP_EXTERNAL_EXPOSED_PAYLOAD,
						...eventPayload,
						actionSubjectId: eventPayload.messageId,
						attributes: {
							...EP_EXTERNAL_EXPOSED_PAYLOAD.attributes,
							...(eventPayload.locale && { locale: eventPayload.locale }),
							...attributes,
						},
					}).fire(EP_ANALYTICS_CHANNEL);
				}
			},
			[createAnalyticsEvent],
		);

		return {
			fireAnalyticsEvent,
		};
	};
